import { fetchReportsTransactions, patchTransaction, listAccounts, getMe, type ReportsTransactionsResponse, type ReportsTransactionRow } from '../api-client';
import { showCategoryPicker } from '../pages/category-picker';
import { openRefundSheet } from './refund-sheet';
import { showToast } from './toast';

interface SheetProps {
  month: string;
  leafId: string;
  onClose?: () => void;
}

interface SheetState {
  loading: boolean;
  data: ReportsTransactionsResponse | null;
  error: string | null;
  rows: ReportsTransactionRow[];
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function formatAed(n: number): string {
  return `AED ${Math.round(n).toLocaleString('en-AE')}`;
}

function formatDay(day: string): string {
  const [yStr, mStr, dStr] = day.split('-');
  const date = new Date(Date.UTC(Number(yStr), Number(mStr) - 1, Number(dStr)));
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function headerHtml(d: ReportsTransactionsResponse, currentTotal: number, currentCount: number): string {
  const title = d.parent_name
    ? `${escapeHtml(d.parent_name)} / ${escapeHtml(d.leaf_name)}`
    : escapeHtml(d.leaf_name);
  const subline = `${currentCount} ${currentCount === 1 ? 'transaction' : 'transactions'} · ${formatAed(currentTotal)}`;
  return `
    <header class="sheet-header tx-list-header">
      <h3>${title}</h3>
      <div class="tx-list-subline">${escapeHtml(subline)}</div>
    </header>
  `;
}

function rowHtml(r: ReportsTransactionRow): string {
  const label = r.merchant ?? r.description ?? '—';
  const isForeign = r.currency_original !== 'AED';
  const original = isForeign
    ? `<small class="tx-list-original">${escapeHtml(r.currency_original)} ${r.amount_original.toLocaleString('en-AE', { maximumFractionDigits: 0 })}</small>`
    : '';
  const isRefund = r.refund_of_transaction_id !== null;
  const marker = isRefund ? `<span class="tx-list-refund-tag">Refund</span>` : '';
  return `
    <button class="tx-list-row" data-tx-id="${escapeHtml(r.id)}" type="button">
      <span class="tx-list-day">${escapeHtml(formatDay(r.day))}</span>
      <span class="tx-list-label">${marker}${escapeHtml(label)}${original ? `<br/>${original}` : ''}</span>
      <span class="tx-list-amt">${escapeHtml(formatAed(r.amount_aed))}</span>
    </button>
  `;
}

function bodyHtml(state: SheetState): string {
  if (state.loading) {
    return `
      <div class="tx-list-skeleton">
        <div class="sk tx-list-sk-row"></div>
        <div class="sk tx-list-sk-row"></div>
        <div class="sk tx-list-sk-row"></div>
      </div>
    `;
  }
  if (state.error) {
    return `
      <div class="tx-list-error">
        <p>${escapeHtml(state.error)}</p>
        <button class="ghost" type="button">Retry</button>
      </div>
    `;
  }
  if (!state.data || state.rows.length === 0) {
    return `<p class="tx-list-empty">No transactions in this category for ${escapeHtml(state.data?.month ?? '')}.</p>`;
  }
  return `<div class="tx-list-rows">${state.rows.map(rowHtml).join('')}</div>`;
}

export function openTransactionListSheet(root: HTMLElement, props: SheetProps): void {
  const state: SheetState = { loading: true, data: null, error: null, rows: [] };

  const close = (): void => {
    root.innerHTML = '';
    props.onClose?.();
  };

  const computeTotal = (): number => state.rows.reduce((s, r) => s + r.amount_aed, 0);

  const render = (): void => {
    const headerSection = state.data
      ? headerHtml(state.data, computeTotal(), state.rows.length)
      : `<header class="sheet-header tx-list-header"><h3>Loading…</h3></header>`;
    root.innerHTML = `
      <div class="sheet-backdrop">
        <div class="sheet tx-list-sheet" role="dialog" aria-modal="true">
          ${headerSection}
          <div class="sheet-body tx-list-body">${bodyHtml(state)}</div>
          <footer class="sheet-footer">
            <button class="ghost" type="button">Close</button>
          </footer>
        </div>
      </div>
    `;
    root.querySelector<HTMLElement>('.sheet-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    root.querySelector<HTMLButtonElement>('.sheet-footer button.ghost')!.addEventListener('click', close);
    const retryBtn = root.querySelector<HTMLButtonElement>('.tx-list-error button.ghost');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        state.loading = true;
        state.error = null;
        render();
        void load();
      });
    }
    root.querySelectorAll<HTMLButtonElement>('button.tx-list-row').forEach((btn) => {
      const txId = btn.getAttribute('data-tx-id')!;
      const row = state.rows.find((r) => r.id === txId);
      if (!row) return;
      btn.addEventListener('click', () => {
        void onRowTap(row);
      });
    });
  };

  function actionMenuHtml(canRefund: boolean): string {
    return `
      <div class="picker-backdrop" id="action-backdrop">
        <div class="picker">
          <h3>Transaction</h3>
          <ul class="picker-list">
            <li class="picker-row" data-action="recategorise"><span>Recategorise</span></li>
            ${canRefund ? `<li class="picker-row" data-action="refund"><span>Add refund</span></li>` : ''}
          </ul>
          <button class="picker-cancel ghost" id="action-cancel">Cancel</button>
        </div>
      </div>
    `;
  }

  function onRowTap(row: ReportsTransactionRow): void {
    const canRefund = row.amount_aed > 0 && row.refund_of_transaction_id === null;
    const menuMount = document.createElement('div');
    menuMount.innerHTML = actionMenuHtml(canRefund);
    root.appendChild(menuMount);
    const closeMenu = () => menuMount.remove();
    menuMount.querySelector<HTMLElement>('#action-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) closeMenu();
    });
    menuMount.querySelector<HTMLButtonElement>('#action-cancel')!.addEventListener('click', closeMenu);
    menuMount.querySelectorAll<HTMLElement>('.picker-row').forEach((li) => {
      li.addEventListener('click', () => {
        const action = li.getAttribute('data-action');
        closeMenu();
        if (action === 'recategorise') void recategorise(row);
        else if (action === 'refund') void startRefund(row);
      });
    });
  }

  async function recategorise(row: ReportsTransactionRow): Promise<void> {
    await showCategoryPicker(
      async (newCategoryId, newSubcategoryId) => {
        if (newCategoryId === row.category_id && newSubcategoryId === row.subcategory_id) return;
        const idx = state.rows.findIndex((r) => r.id === row.id);
        const removed = idx >= 0 ? state.rows.splice(idx, 1)[0]! : null;
        render();
        try {
          await patchTransaction(
            row.id,
            { category_id: newCategoryId, subcategory_id: newSubcategoryId },
            { learnMerchant: false },
          );
          await reload();
        } catch (e) {
          if (removed) state.rows.splice(idx, 0, removed);
          render();
          const msg = e instanceof Error ? e.message : 'Could not save change';
          showToast(msg, 'error');
        }
      },
      {
        kind: 'expense',
        currentCategoryId: row.category_id,
        currentSubcategoryId: row.subcategory_id,
      },
    );
  }

  async function startRefund(row: ReportsTransactionRow): Promise<void> {
    let accounts;
    let defaultFundingAccountId: string | null = null;
    try {
      const [acctRes, me] = await Promise.all([listAccounts(), getMe()]);
      accounts = acctRes.accounts;
      defaultFundingAccountId = me.default_funding_account_id;
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Could not load accounts', 'error');
      return;
    }
    const categoryLabel = state.data
      ? (state.data.parent_name ? `${state.data.parent_name} / ${state.data.leaf_name}` : state.data.leaf_name)
      : '';
    const sheetMount = document.createElement('div');
    root.appendChild(sheetMount);
    openRefundSheet(sheetMount, {
      original: {
        id: row.id,
        amount_original: row.amount_original,
        currency_original: row.currency_original,
        from_account_id: row.from_account_id,
        categoryLabel,
      },
      accounts,
      defaultFundingAccountId,
      onClose: () => sheetMount.remove(),
      onSuccess: () => { sheetMount.remove(); void reload(); },
    });
  }

  async function reload(): Promise<void> {
    try {
      const data = await fetchReportsTransactions(props.month, props.leafId);
      state.data = data;
      state.rows = [...data.rows];
      state.error = null;
    } catch (e) {
      state.error = e instanceof Error ? e.message : String(e);
    }
    state.loading = false;
    render();
  }

  async function load(): Promise<void> {
    await reload();
  }

  render();
  void load();
}
